import * as CONSTANT from '../constants';
import { ItemDetails } from '../models/item.model';
import { itemBuckets } from '../priceCalculation/buckets';
import {
  addAdditionalPrice,
  calculateAndUpdateTotalAdditionalPrice,
} from '../priceCalculation/itemsAsModifierPrice';
import { bucketKeyForNonRequiredTray } from './helperMethods';

export function isSandwichTrayItem(item: unknown): boolean {
  return (item as { is_sandwich_tray?: number })?.is_sandwich_tray === 1;
}

export function getSingleItemTraysInPackage(packageItem: any) {
  return (
    packageItem?.package_categories
      ?.filter((tray: any) => tray.items?.length === 1)
      .map((tray: any) => tray.items[0]) ?? []
  );
}

export function resolveSubItemBucketSlotForTray(
  packageRoot: any,
  trayItem: any,
  order: any,
  manualSelectedItem?: ItemDetails,
  manualSelectedItemId?: number
): number {
  if (!packageRoot?.is_package) {
    return CONSTANT.SINGLE_ITEM_SELECTED_SECTION;
  }
  if (
    manualSelectedItem &&
    manualSelectedItemId != null &&
    trayItem?.id != null &&
    trayItem.id === manualSelectedItem.id
  ) {
    return manualSelectedItemId;
  }
  const key = bucketKeyForNonRequiredTray(order, trayItem?.id);
  if (key >= 1) {
    return key;
  }
  const trays = getSingleItemTraysInPackage(packageRoot);
  const idx = trays.findIndex((t: any) => t.id === trayItem?.id);
  return idx >= 0 ? idx + 1 : CONSTANT.SINGLE_ITEM_SELECTED_SECTION;
}

export function resolveParentTrayItemForChips(
  packageRoot: any,
  fromItem: number,
  order: any,
  manualSelectedItem?: ItemDetails,
  manualSelectedItemId?: number
): ItemDetails | null {
  if (!packageRoot) {
    return null;
  }
  if (!packageRoot.is_package) {
    return fromItem === CONSTANT.SINGLE_ITEM_SELECTED_SECTION
      ? packageRoot
      : null;
  }
  if (
    manualSelectedItem &&
    manualSelectedItemId != null &&
    fromItem === manualSelectedItemId
  ) {
    return manualSelectedItem;
  }
  const trays = getSingleItemTraysInPackage(packageRoot);
  const byIndex = trays[fromItem - 1];
  if (byIndex) {
    return byIndex;
  }
  const cat = packageRoot.package_categories?.find((c: any) => {
    if (c.items?.length !== 1) {
      return false;
    }
    return bucketKeyForNonRequiredTray(order, c.items[0].id) === fromItem;
  });
  return cat?.items?.[0] ?? null;
}

export function getSelectedSandwichChipsModifierId(
  fromItem: number,
  childItemGroupId: number | undefined
): number | null {
  if (!childItemGroupId) {
    return null;
  }
  const mods =
    itemBuckets.specificItemBucketSelectedModifiers(
      CONSTANT.REQUIRED_MODIFIERS,
      fromItem
    ) || [];
  const selected = mods.find(
    (m: any) =>
      m.related_modifier_group === childItemGroupId &&
      (m.quantity ?? 0) > 0
  );
  return selected?.modifier_count ?? null;
}

export function resolveSandwichTrayChipsMax(
  parentTrayItem: any,
  fromItem: number,
  childItemID: number
): number | null {
  if (!isSandwichTrayItem(parentTrayItem)) {
    return null;
  }
  return getSelectedSandwichChipsModifierId(fromItem, childItemID);
}

/**
 * Validates items-as-modifiers (sub-item required groups) for a single standalone tray line item.
 * Package flows are out of scope — always uses {@link CONSTANT.SINGLE_ITEM_SELECTED_SECTION} for chips max.
 */
export function areStandaloneSubItemModifierSelectionsComplete({
  trayItem,
  order,
  itemsAsModifier,
  isCustomizeSectionExposed,
}: {
  trayItem: any;
  order: { size?: string | null };
  itemsAsModifier: any[];
  isCustomizeSectionExposed: boolean;
  }): boolean {
  const groups = trayItem?.sub_items_required_modifier_groups as
    | any[]
    | undefined;
  if (!groups?.length) {
    return true;
  }
  const size = order?.size;
  if (!size) {
    return false;
  }

  const fromItem = CONSTANT.SINGLE_ITEM_SELECTED_SECTION;

  for (const g of groups) {
    let requiredCount =
      size === CONSTANT.HALF_SIZE.id
        ? g.half_item_count
        : g.full_item_count;
    if (requiredCount == null || Number.isNaN(Number(requiredCount))) {
      const rows =
        size === CONSTANT.HALF_SIZE.id ? g.items_size_one : g.items_size_two;
      requiredCount =
        (rows ?? []).reduce(
          (acc: number, r: { item_count?: number }) =>
            acc + (r.item_count ?? 0),
          0
        ) * (g.item_counter_increment ?? 1);
    }

    if (isSandwichTrayItem(trayItem)) {
      const chipsMax = resolveSandwichTrayChipsMax(trayItem, fromItem, g.id);
      if (chipsMax === 0) {
        continue;
      }
      if (chipsMax != null) {
        requiredCount = chipsMax;
      }
    }

    // Prefer live selection state for validation even when customization UI is hidden.
    // The state is seeded with defaults and keeps button enablement consistent.
    const modifierRow = itemsAsModifier.find((row: any) => row.id === g.id);
    if (modifierRow) {
      const selectedTotal = (modifierRow.selectedItems ?? []).reduce(
        (acc: number, sub: any) => acc + (sub.quantity ?? 0),
        0
      );
      if (Number(requiredCount) !== selectedTotal) {
        return false;
      }
      continue;
    }

    if (isCustomizeSectionExposed) {
      return false;
    }

    {
      const sizeRows =
        size === CONSTANT.HALF_SIZE.id ? g.items_size_one : g.items_size_two;
      const byDefault =
        (sizeRows ?? []).reduce(
          (acc: number, sub: { item_count?: number }) =>
            acc + (sub.item_count ?? 0),
          0
        ) * (g.item_counter_increment ?? 1);
      if (Number(requiredCount) !== byDefault) {
        return false;
      }
    }
  }
  return true;
}

export function shouldHideTrayChipsGroup(
  parentTrayItem: any,
  fromItem: number,
  relatedSubItemGroupId: number
): boolean {
  if (!isSandwichTrayItem(parentTrayItem) || !relatedSubItemGroupId) {
    return false;
  }
  return (
    resolveSandwichTrayChipsMax(
      parentTrayItem,
      fromItem,
      relatedSubItemGroupId
    ) === 0
  );
}

export function filterVisibleSubItemModifierGroups(
  groups: any[] | undefined,
  parentTrayItem: any,
  fromItem: number
): any[] {
  if (!groups?.length) {
    return groups || [];
  }
  if (!isSandwichTrayItem(parentTrayItem)) {
    return groups;
  }
  return groups.filter(
    (g: any) =>
      resolveSandwichTrayChipsMax(parentTrayItem, fromItem, g.id) !== 0
  );
}

export function trimSandwichTrayChipsSelections(params: {
  itemsAsModifier: any[];
  parentTrayItem: any;
  newChipsMax: number;
  order: any;
  dispatch: (action: unknown) => void;
  setItemsAsModifier: (v: any) => void;
  relatedGroup?: number;
}): void {
  const {
    itemsAsModifier,
    parentTrayItem,
    newChipsMax,
    order,
    dispatch,
    setItemsAsModifier,
    relatedGroup,
  } = params;
  if (!parentTrayItem) {
    return;
  }
  const groupDef = parentTrayItem.sub_items_required_modifier_groups?.find(
    (g: any) => g.id === relatedGroup
  );
  if (!groupDef) {
    return;
  }
  const entryIdx = itemsAsModifier.findIndex(
    (row) =>
      row.id === relatedGroup && row.parentItem?.id === parentTrayItem.id
  );
  if (entryIdx < 0) {
    return;
  }
  calculateAndUpdateTotalAdditionalPrice(itemsAsModifier, '-', dispatch);
  const row = itemsAsModifier[entryIdx];
  let selectedItems = [...row.selectedItems];
  const inc = groupDef.item_counter_increment || 1;

  if (newChipsMax <= 0) {
    selectedItems = [];
  } else if (
    (groupDef.items_as_modifier?.length || 0) > 0 ||
    selectedItems.length > 0
  ) {
    const rowItemCount = groupDef.items_as_modifier?.length || 0;
    const divisor =
      rowItemCount > 0 ? rowItemCount : selectedItems.length || 1;
    const perItemShare = Math.floor(newChipsMax / divisor);
    const countVal = Math.floor(perItemShare / inc);
    const quantityEach = countVal * inc;
    const source =
      rowItemCount > 0 ? groupDef.items_as_modifier : selectedItems;
    selectedItems = source.map((item: any) => ({
      ...item,
      quantity: quantityEach,
      size_one_count: countVal,
      size_two_count: countVal,
      isSelected: true,
      ...(item.is_selected_with_count
        ? { isByDefault: true, actualQuantity: inc * countVal }
        : {}),
    }));
  }

  const repriced = addAdditionalPrice({
    items: selectedItems,
    currentItemsGroup: groupDef,
    order,
    relatedGroup,
  });
  const next = [...itemsAsModifier];
  next[entryIdx] = { ...row, selectedItems: repriced };
  calculateAndUpdateTotalAdditionalPrice(next, '+', dispatch);
  setItemsAsModifier(next);
}
